import React from "react";
import {
  Droplets,
  ShieldCheck,
  Filter,
  Waves,
  CheckCircle2,
  ArrowRight,
  Factory,
  FlaskConical,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import reverseosmosis from "../images/reveseosmosis.png";
import waterTreatment from "../images/watertreatment.png";
import { assets } from "../data/siteData";
const treatmentImages = {
  hero: "https://cdn11.bigcommerce.com/s-1x0ys7yqwf/product_images/uploaded_images/ro-400-173k-web-front.1.png",
  ro1: "https://www.netsolwater.com/netsol-water/assets/img/product-images/Are_Stainless_Steel_pipes_used_in_Commercial_RO_Plants.jpg",
  ro2: "https://d91ztqmtx7u1k.cloudfront.net/ClientContent/Images/ExtraLarge/ro-capacity-1000literhour-commercial-ro-water-treatmen20230916105843.jpeg",
  uf1: "https://www.hongtekfiltration.com/uploadfile/2019/1212/20191212050411662.jpg",
  carbon1: "https://www.eurowater.com/admin/public/getimage.ashx?Crop=2&Format=jpg&Image=%2FFiles%2FImages%2Feurowater%2FCommon%2FOn-site%2FActivated-carbon-filter-filtration-topbanner-Eurowater.jpg&Quality=75&Width=1278",
  uv1: "https://image.made-in-china.com/2f0j00QNBtEGPCuucR/Chemical-Free-Ultraviolet-Water-Disinfection-with-Ss-Reactor.webp",
  stp1: "https://alantech.in/storage/blogs/modern-sewage-treatment-plant.webp",
  iron1: "https://www.neyawatersolutions.com/img/i1.png",
};

const treatmentTypes = [
  {
    title: "Reverse Osmosis Plants",
    desc: "High-performance RO systems for drinking water purification, dissolved solids reduction, and safe water delivery in institutional, industrial, and community applications.",
    icon: Droplets,
  },
  {
    title: "Ultrafiltration Systems",
    desc: "UF systems for removing suspended solids, bacteria, and turbidity, often used as standalone treatment or pre-treatment before RO.",
    icon: Filter,
  },
  {
    title: "Activated Carbon Filtration",
    desc: "Carbon-based filtration for chlorine removal, odor control, and reduction of organic impurities in water treatment trains.",
    icon: FlaskConical,
  },
  {
    title: "UV Disinfection",
    desc: "Final-stage disinfection systems for controlling bacterial contamination and improving microbiological safety without chemical residue.",
    icon: ShieldCheck,
  },
  {
    title: "Wastewater / STP Systems",
    desc: "Sewage and wastewater treatment solutions for reuse, compliance, and improved water management across buildings and industrial sites.",
    icon: Factory,
  },
  {
    title: "Iron Removal Plants",
    desc: "Groundwater treatment systems designed to remove iron, manganese, turbidity, and odor from borewell and source water.",
    icon: Waves,
  },
];

const benefits = [
  "Customized design based on source water quality",
  "Integration of filtration, RO, UV, and dosing technologies",
  "Systems for communities, institutions, buildings, and industry",
  "Compact, skid-mounted, and field-deployable options",
  "Designed for performance, reliability, and maintenance ease",
  "Can be linked with dispensing systems and monitoring dashboards",
];

const applications = [
  "Drinking water plants across Kolkata and West Bengal",
  "Water ATM and kiosk infrastructure in Newtown and Rajarhat",
  "Schools, hospitals, and institutions",
  "Commercial and residential buildings in Kolkata",
  "Industrial process water in West Bengal",
  "Groundwater and rural water systems",
];

export default function WaterTreatmentPage() {
  return (
    <>
      <SEO
        title="Water Treatment Plant in Kolkata, Newtown, Rajarhat | Bacteria Removal & RO Systems"
        description="Hydrotec Solutions installs water treatment plants, RO systems, UV disinfection, and bacteria removal systems in Kolkata, Newtown, Rajarhat, and across West Bengal. Get clean, safe drinking water for homes, institutions, and industries."
        keywords="water treatment plant Kolkata, water purification Newtown, bacteria removal water Kolkata, RO plant Rajarhat, UV disinfection West Bengal, iron removal plant Kolkata, water treatment company Kolkata, ultrafiltration system West Bengal, drinking water purification Kolkata"
        canonical="https://hydroteclimited.com/solutions/water-treatment"
      />
      <PageHero
        title="Water Treatment Solutions"
        text="In a world where water quality, availability and security are increasingly at risk, Hydrotec Solutions helps reshape systems and strategies—so that resources are safeguarded, infrastructure is resilient, and communities can thrive.

"
        image={assets.testing}
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-cyan-700">
              Water Treatment Expertise
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
              Complete treatment systems for safer, cleaner, and more reliable water.
            </h2>
            <p className="mt-6 text-lg leading-8 text-slate-600">
              Hydrotec designs treatment solutions based on source water
              condition, application need, output quality target, and operating
              environment. From drinking water purification to wastewater and
              groundwater treatment, we combine technologies into working
              systems that are practical in the field.
            </p>
            <p className="mt-4 leading-8 text-slate-600">
              Our treatment approach may include sand filtration, activated
              carbon filtration, ultrafiltration, reverse osmosis, UV
              disinfection, chlorination, iron removal, and wastewater reuse
              process lines. We serve projects across Kolkata, Newtown,
              Rajarhat, and throughout West Bengal.
            </p>

            <div className="mt-8 grid gap-3">
              {benefits.slice(0, 3).map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-cyan-700" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl">
            <img
              src={reverseosmosis}
              alt="Reverse osmosis treatment plant"
              className="h-[460px] w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-cyan-700">
              Treatment Categories
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
              Different types of water treatment solutions for different water challenges.
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {treatmentTypes.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-3xl border border-slate-200 bg-slate-50 p-7 shadow-sm"
                >
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-cyan-700">
            Technology Gallery
          </p>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
            Treatment systems and technologies used across modern water infrastructure.
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <img
              src={treatmentImages.ro2}
              alt="Commercial RO water treatment system"
              className="h-72 w-full object-cover"
            />
            <div className="p-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Reverse Osmosis System
              </h3>
              <p className="mt-2 leading-7 text-slate-600">
                RO systems for drinking water purification, dissolved solids
                reduction, and high-quality treated water output.
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <img
              src={treatmentImages.uf1}
              alt="Ultrafiltration system"
              className="h-72 w-full object-cover"
            />
            <div className="p-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Ultrafiltration Unit
              </h3>
              <p className="mt-2 leading-7 text-slate-600">
                UF systems support bacteria and suspended solids removal and are
                often used as strong pre-treatment stages.
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <img
              src={treatmentImages.carbon1}
              alt="Activated carbon filtration system"
              className="h-72 w-full object-cover"
            />
            <div className="p-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Activated Carbon Filtration
              </h3>
              <p className="mt-2 leading-7 text-slate-600">
                Carbon filters are widely used for chlorine removal, odor
                reduction, and organic contaminant control.
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <img
              src={treatmentImages.uv1}
              alt="UV disinfection system"
              className="h-72 w-full object-cover"
            />
            <div className="p-5">
              <h3 className="text-lg font-semibold text-slate-900">
                UV Disinfection
              </h3>
              <p className="mt-2 leading-7 text-slate-600">
                UV systems provide an effective final-stage disinfection barrier
                for drinking water safety.
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <img
              src={treatmentImages.stp1}
              alt="Sewage treatment plant"
              className="h-72 w-full object-cover"
            />
            <div className="p-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Wastewater / STP System
              </h3>
              <p className="mt-2 leading-7 text-slate-600">
                STP systems help treat sewage and wastewater for safer disposal,
                reuse, and better water stewardship.
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <img
              src={treatmentImages.iron1}
              alt="Iron removal treatment plant"
              className="h-72 w-full object-cover"
            />
            <div className="p-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Iron Removal Plant
              </h3>
              <p className="mt-2 leading-7 text-slate-600">
                Designed for groundwater treatment, these systems help reduce
                iron, manganese, turbidity, and odor.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-100/60">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
            <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
              <Filter className="h-7 w-7" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">
              Key Design Considerations
            </h3>
            <div className="mt-6 space-y-3">
              {benefits.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-cyan-700" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
            <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
              <Waves className="h-7 w-7" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">
              Typical Applications
            </h3>
            <div className="mt-6 space-y-3">
              {applications.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-cyan-700" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-cyan-700">
              Why Hydrotec
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
              We combine multiple technologies into one complete treatment solution.
            </h2>
            <p className="mt-6 text-lg leading-8 text-slate-600">
              Hydrotec’s strength is in designing treatment lines that are
              practical for site conditions and output requirements. Instead of
              treating each component in isolation, we build integrated systems
              that connect source water, treatment stages, storage, dispensing,
              and monitoring into one dependable solution.
            </p>
          </div>

          <div className="rounded-[2rem] bg-cyan-800 p-8 text-white shadow-xl shadow-cyan-900/20 md:p-10">
            <h3 className="text-2xl font-bold">
              Need a water treatment solution for your site?
            </h3>
            <p className="mt-4 leading-8 text-cyan-50">
              We can support drinking water treatment, RO systems, UF, UV,
              activated carbon filtration, iron removal, wastewater treatment,
              and integrated treatment + dispensing deployments.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-cyan-800"
            >
              Discuss Your Requirement <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}